import { useMutation, useQueryClient } from '@tanstack/react-query';
import { busUpdate } from '@trafiku/contracts';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { BusMap } from '@/components/bus-track';
import { LineCheckNotice } from '@/components/dashboard-widgets';
import { FormActions } from '@/components/form-actions';
import { GeoJsonLayer, lineLayer } from '@/components/map/layers';
import { LiveBusesLayer } from '@/components/map/live-buses';
import { MapView } from '@/components/map/map-view';
import { ResourceList } from '@/components/resource-list';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Badge, Card, ColorDot, PageHeader, Spinner } from '@/components/ui/layout';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useFormState } from '@/lib/form';
import { t, tn } from '@/lib/i18n';
import { type LiveBus, useLiveBuses } from '@/lib/live-buses';
import { linesCollection, useNetwork } from '@/lib/network';
import { useAll, useItem, useList, useSave } from '@/lib/resources';
import { useLineChecks } from '@/lib/stats';
import type { Bus, Zone } from '@/lib/types';

function useAssignZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, zone }: { id: string; zone: string | null }) =>
      api<Bus>(`/buses/${id}/zone`, { method: 'PUT', body: { zone } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buses'] });
      toast.success(t('Line updated'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not assign')),
  });
}

function ZoneSelect({ bus, zones }: { bus: Bus; zones: Zone[] }) {
  const assign = useAssignZone();
  if (bus.vehicle_type === 'other')
    return <span className="text-sm text-slate-500">{bus.vehicle_note || t('Not a bus')}</span>;
  return (
    <Select
      className="w-48"
      value={bus.zone?._id ?? ''}
      disabled={assign.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => assign.mutate({ id: bus._id, zone: e.target.value || null })}
    >
      <option value="">{t('Out of service')}</option>
      {zones.map((z) => (
        <option key={z._id} value={z._id}>
          {z.bus_sign} · {z.name}
        </option>
      ))}
    </Select>
  );
}

const status = (b: Bus) =>
  b.vehicle_type === 'other' ? (
    <Badge tone="gray">{t('Not a bus')}</Badge>
  ) : b.vehicle_type === 'new' && !b.zone ? (
    <Badge tone="amber">{t('New')}</Badge>
  ) : !b.zone ? (
    <Badge>{t('No line')}</Badge>
  ) : !b.is_visible ? (
    <Badge tone="amber">{t('Hidden')}</Badge>
  ) : b.is_active ? (
    <Badge tone="green">{t('On route')}</Badge>
  ) : (
    <Badge tone="blue">{t('At terminus')}</Badge>
  );

type View = 'bus' | 'new' | 'other' | 'all';
const VIEWS: { view: View; label: string }[] = [
  { view: 'bus', label: t('Buses') },
  { view: 'new', label: t('New devices') },
  { view: 'other', label: t('Other vehicles') },
  { view: 'all', label: t('All') },
];

/** Devices Arvento reported for the first time and nobody has sorted yet. */
function useNewDevices() {
  return useList<Bus>('buses', { type: 'new', pageSize: 1 });
}

export function BusesPage() {
  const network = useNetwork();
  const live = useLiveBuses();
  const navigate = useNavigate();
  const zones = useAll<Zone>('zones', { sort: 'sort' });
  const [view, setView] = useState<View>('bus');
  const fresh = useNewDevices().data?.total ?? 0;
  return (
    <ResourceList<Bus>
      key={view}
      title={t('Buses')}
      subtitle={t('Vehicles reported by Arvento. Assign a bus to a line to show it in the app.')}
      params={{ type: view }}
      toolbar={
        <div className="mb-4 space-y-3">
          {fresh > 0 && view !== 'new' && (
            <button
              type="button"
              onClick={() => setView('new')}
              className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-900 hover:bg-amber-100"
            >
              {tn(
                fresh,
                'Arvento reports {n} new device: assign it a line, or mark it as not a bus.',
                'Arvento reports {n} new devices: assign them a line, or mark them as not a bus.',
              )}
              <span className="ml-auto font-medium">{t('Show')} →</span>
            </button>
          )}
          <div className="flex gap-1 border-b border-slate-200">
            {VIEWS.map((v) => (
              <button
                key={v.view}
                type="button"
                onClick={() => setView(v.view)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                  view === v.view
                    ? 'border-brand-600 font-medium text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {v.label}
                {v.view === 'new' && fresh > 0 && (
                  <span className="ml-1 text-xs text-amber-700">{fresh}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      }
      map={({ rows, hovered, hover }) => (
        <MapView className="h-full">
          <GeoJsonLayer data={linesCollection(network.data?.lines)} layers={[lineLayer(undefined, 3, 0.4)]} />
          <LiveBusesLayer
            // Devices without a line are not in the live feed: their stored positions instead.
            buses={
              view === 'bus' ? live.buses : (rows ?? []).filter((b) => b.geometry).map((b) => asLiveBus(b))
            }
            showHidden
            highlight={hovered}
            onSelect={(id) => {
              hover(id);
              navigate(`/buses/${id}`);
            }}
          />
        </MapView>
      )}
      resource="buses"
      basePath="/buses"
      canCreate={false}
      pageSize={50}
      filters={(set, v) => (
        <Select
          className="w-56"
          value={(v.zone as string) ?? ''}
          onChange={(e) => set('zone', e.target.value)}
        >
          <option value="">{t('All lines')}</option>
          <option value="null">{t('Out of service')}</option>
          {zones.data?.items.map((z) => (
            <option key={z._id} value={z._id}>
              {z.bus_sign} · {z.name}
            </option>
          ))}
        </Select>
      )}
      columns={[
        {
          header: t('Bus'),
          cell: (b) => (
            <span className="flex items-center gap-2">
              <ColorDot color={b.color} />
              <span className="font-medium">{b.name || b.arvento_id}</span>
              {b.name && <span className="text-xs text-slate-400">{b.arvento_id}</span>}
            </span>
          ),
        },
        { header: t('Line'), cell: (b) => <ZoneSelect bus={b} zones={zones.data?.items ?? []} /> },
        { header: t('Direction'), cell: (b) => b.line?.name ?? '—' },
        { header: t('Status'), cell: status },
        { header: t('Avg speed'), cell: (b) => (b.avg_15min ? `${b.avg_15min.toFixed(1)} km/h` : '—') },
        {
          header: t('Location'),
          cell: (b) => <span className="text-xs text-slate-500">{b.address || '—'}</span>,
        },
      ]}
    />
  );
}

export function BusEditPage() {
  const { id } = useParams();
  const item = useItem<Bus>('buses', id);
  const zones = useAll<Zone>('zones', { sort: 'sort' });
  const _network = useNetwork();
  const save = useSave<Bus>('buses', id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const form = useFormState({ name: '', description: '', vehicle_type: 'bus', vehicle_note: '' });
  const live = useLiveBuses();
  const checks = useLineChecks();
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    if (item.data)
      form.setValues({
        name: item.data.name ?? '',
        description: item.data.description ?? '',
        vehicle_type: item.data.vehicle_type ?? 'bus',
        vehicle_note: item.data.vehicle_note ?? '',
      });
  }, [item.data]);
  if (item.isLoading || !item.data) return <Spinner />;
  const bus = item.data;
  const pos = bus.geometry?.coordinates as [number, number] | undefined;
  const liveBus = live.buses.find((b) => b._id === bus._id);
  const flagged = checks.data?.items.find((b) => b._id === bus._id);

  return (
    <>
      <PageHeader
        title={bus.name || bus.arvento_id}
        subtitle={t('Arvento ID {id}', { id: bus.arvento_id ?? '' })}
        back="/buses"
        actions={status(bus)}
      />
      <div className="grid items-start gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            // A new device stays "new" until it gets a line or a type is picked.
            const { vehicle_type, ...rest } = form.values;
            const values = vehicle_type === 'new' ? rest : form.values;
            if (await form.submit(busUpdate, (d) => save.mutateAsync(d), values)) navigate('/buses');
          }}
        >
          <LineCheckNotice busId={bus._id} check={flagged?.line_check} zone={flagged?.zone ?? null} />
          <Card>
            <div className="space-y-4">
              <Field label={t('Line')}>
                {() => <ZoneSelect bus={bus} zones={zones.data?.items ?? []} />}
              </Field>
              <Field
                label={t('Type')}
                hint={
                  form.values.vehicle_type === 'other'
                    ? t('Not shown in the apps, statistics, checks or alerts. Its line is removed on saving.')
                    : undefined
                }
              >
                {(fid) => (
                  <Select
                    id={fid}
                    value={form.values.vehicle_type}
                    onChange={(e) => form.set('vehicle_type', e.target.value)}
                    disabled={!can('admin')}
                  >
                    {form.values.vehicle_type === 'new' && (
                      <option value="new">{t('New: not sorted yet')}</option>
                    )}
                    <option value="bus">{t('A bus')}</option>
                    <option value="other">{t('Not a bus')}</option>
                  </Select>
                )}
              </Field>
              {form.values.vehicle_type === 'other' && (
                <Field label={t('Note')}>
                  {(fid) => (
                    <Input
                      id={fid}
                      value={form.values.vehicle_note}
                      placeholder={t('e.g. service car')}
                      onChange={(e) => form.set('vehicle_note', e.target.value)}
                      disabled={!can('admin')}
                    />
                  )}
                </Field>
              )}
              <Field label={t('Name')}>
                {(fid) => (
                  <Input
                    id={fid}
                    value={form.values.name}
                    onChange={(e) => form.set('name', e.target.value)}
                    disabled={!can('admin')}
                  />
                )}
              </Field>
              <Field label={t('Description')}>
                {(fid) => (
                  <Textarea
                    id={fid}
                    value={form.values.description}
                    onChange={(e) => form.set('description', e.target.value)}
                    disabled={!can('admin')}
                  />
                )}
              </Field>
              <dl className="grid grid-cols-2 gap-2 rounded-md bg-slate-50 p-3 text-xs">
                <dt className="text-slate-500">{t('Direction')}</dt>
                <dd>{bus.line?.name ?? '—'}</dd>
                <dt className="text-slate-500">{t('Speed')}</dt>
                <dd>{bus.speed ?? 0} km/h</dd>
                <dt className="text-slate-500">{t('Avg (15 min)')}</dt>
                <dd>{bus.avg_15min ? `${bus.avg_15min.toFixed(1)} km/h` : '—'}</dd>
                <dt className="text-slate-500">{t('Heading')}</dt>
                <dd>{bus.heading ?? 0}°</dd>
                <dt className="text-slate-500">{t('Address')}</dt>
                <dd>{bus.address || '—'}</dd>
              </dl>
            </div>
            <div className="mt-6">
              <FormActions
                saving={save.isPending}
                resource="buses"
                id={id}
                backTo="/buses"
                canDelete={can('admin')}
                canSave={can('admin')}
                deleteMessage={t('The bus is created again automatically if Arvento keeps reporting it.')}
              />
            </div>
          </Card>
        </form>
        <BusMap busId={bus._id} zoneId={bus.zone?._id} bus={pos ? (liveBus ?? asLiveBus(bus)) : undefined} />
      </div>
    </>
  );
}

/** The stored bus as a live-feed entry (used until the feed reports it). */
function asLiveBus(bus: Bus): LiveBus {
  return {
    _id: bus._id,
    color: bus.color,
    number: bus.number,
    heading: bus.heading,
    is_active: bus.is_active,
    is_visible: true,
    geometry: bus.geometry as LiveBus['geometry'],
  };
}
